import React, { useEffect, useRef } from 'react';

import type { BoardOperation } from './editor';
import { CONTENT, SIZED, heightPatch, minContentFor, presetsFor } from './ResizeHandle';
import { type Block, type BlockFamily, type Row, isColumn } from './types';
import type { BoardEditor } from './useBoardEdit';

const SNAP_PX = 6;

type Sized = { block: Block; node: HTMLElement; content: number; style: string };

export default function RowHeight({ row, host, editor, familyOf }: { row: Row; host: React.RefObject<HTMLDivElement>; editor: BoardEditor; familyOf: (id: string) => BlockFamily | undefined }) {
  const drag = useRef<{ startY: number; sized: Array<Sized> } | undefined>(undefined);
  const heightOf = (entry: Sized, dy: number) => {
    const family = familyOf(entry.block.id);
    const content = Math.max(minContentFor(family), Math.round(entry.content + dy));
    return presetsFor(family).find(([, px]) => Math.abs(px - content) <= SNAP_PX) ?? content;
  };
  const restore = () => {
    drag.current?.sized.forEach((entry) => entry.node.setAttribute('style', entry.style));
    drag.current = undefined;
  };
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !drag.current) return;
      event.stopPropagation();
      restore();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []);
  const blocks = row.flatMap((item) => (isColumn(item) ? item : [item])).filter((block) => SIZED.includes(familyOf(block.id)));
  if (!blocks.length) return null;
  return (
    <span
      aria-hidden
      className="row-gap"
      onLostPointerCapture={() => drag.current && restore()}
      onPointerCancel={restore}
      onPointerDown={(event) => {
        if (!host.current || editor.pending) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        const sized = blocks.flatMap((block): Array<Sized> => {
          const node = host.current!.querySelector<HTMLElement>(`[data-lens-block="${CSS.escape(block.id)}"]`);
          const content = node?.querySelector<HTMLElement>(CONTENT)?.getBoundingClientRect().height;
          return node && content ? [{ block, node, content, style: node.getAttribute('style') ?? '' }] : [];
        });
        drag.current = { startY: event.clientY, sized };
      }}
      onPointerMove={(event) => {
        const at = drag.current;
        if (!at) return;
        at.sized.forEach((entry) => {
          const next = heightOf(entry, event.clientY - at.startY);
          entry.node.style.setProperty('--chart-h', `${Array.isArray(next) ? next[1] : next}px`);
        });
      }}
      onPointerUp={(event) => {
        const at = drag.current;
        if (!at) return;
        const dy = event.clientY - at.startY;
        restore();
        if (Math.abs(dy) <= SNAP_PX) return;
        const operations = at.sized.map((entry): BoardOperation => {
          const next = heightOf(entry, dy);
          return { op: 'set', id: entry.block.id, patch: heightPatch(familyOf(entry.block.id), Array.isArray(next) ? { size: next[0] } : { h: next }) };
        });
        editor.apply(operations);
      }}
    />
  );
}
